import { useState, useCallback, useEffect, useRef } from 'react';
import { ActivityFeedEntryDto, FiltersDto } from 'api/activity-feed';
import { activityFeedAPI } from 'services/swagger-api';

export interface ActivityFeedSearchFilters extends FiltersDto {
  contractId?: string;
}

interface UseActivityFeedOptions {
  searchFilters?: ActivityFeedSearchFilters;
  enableInfiniteScroll?: boolean;
}

interface UseActivityFeedReturn {
  entries: ActivityFeedEntryDto[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  hasMore: boolean;
}

export const useActivityFeed = (
  options: UseActivityFeedOptions = {},
  maxItems: number = 10
): UseActivityFeedReturn => {
  const { searchFilters, enableInfiniteScroll = false } = options;

  const [entries, setEntries] = useState<ActivityFeedEntryDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const fetchEntries = useCallback(async (page: number = 1, append: boolean = false) => {
    if (page === 1) {
      setIsLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const response = await activityFeedAPI.searchActivityFeedAsync({
        getGenericListCommandDto: {
          pageNumber: page,
          pageSize: maxItems,
          filters: {
            contractIds: searchFilters?.contractId ? [searchFilters.contractId] : undefined,
            riskId: searchFilters?.riskId,
            ...searchFilters
          }
        }
      });
      const newEntries = response.items || [];
      const total = response.totalCount || 0;

      setTotalCount(total);

      if (append) {
        setEntries((prev) => [...prev, ...newEntries]);
      } else {
        setEntries(newEntries);
      }

      // Calculer s'il y a plus d'éléments à charger
      const loadedCount = append ? entries.length + newEntries.length : newEntries.length;
      setHasMore(loadedCount < total);
    } catch (err) {
      console.error('Error fetching activity feed:', err);
      setError("Erreur lors du chargement du flux d'activite");
      if (!append) {
        setEntries([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [maxItems, searchFilters, entries.length]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !enableInfiniteScroll) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await fetchEntries(nextPage, true);
  }, [currentPage, fetchEntries, hasMore, isLoadingMore, enableInfiniteScroll]);

  const refresh = useCallback(async () => {
    setCurrentPage(1);
    setHasMore(true);
    await fetchEntries(1, false);
  }, [fetchEntries]);

  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchEntries(1, false);
  }, [searchFilters]);

  return {
    entries,
    isLoading,
    isLoadingMore,
    error,
    refresh,
    loadMore,
    hasMore
  };
};
